import { Layout, Rect, Txt, type Node } from "@revideo/2d";
import { isRtl, secondLineFor, type CaptionCard, type CaptionStyle, type Clip } from "@hudhud/core";
import { HEIGHT, activeCard, activeWordIndex, cardEnter, cardLift, cardStartFrame, captionBlock, captionBox, rgba, upperText, wordLook, type WordLook } from "@hudhud/reel/math";
import { CAPTION_SHADOW, family, isRtlText, paragraph, weight } from "./text";

export interface CaptionsInput {
  clip: Clip;
  /** the composition frame */
  frame: () => number;
  /** the composition frame the speech starts on */
  from: number;
  fps: number;
  lite?: boolean;
}

/** One card, built once: its words light up, pop and mark themselves from the clock. Mirrors Card in the Remotion reel. */
function cardNode(clip: Clip, card: CaptionCard, index: number, o: CaptionsInput): Node {
  const style = clip.style;
  const lang = clip.captionLang;
  const rtl = isRtl(lang);
  const block = captionBlock(style);
  const box = captionBox(style);
  const glass = style.box === "glass";
  const em = (n: number) => style.fontSize * n;
  const second = secondLineFor(clip, card.id);
  const speechMs = () => ((o.frame() - o.from) / o.fps) * 1000;
  const speechFrame = () => o.frame() - o.from;
  const active = () => activeCard(clip.captions, speechMs())?.index === index;
  const local = () => speechFrame() - cardStartFrame(card, o.fps);
  const enter = () => cardEnter(style, local(), o.fps);

  // one look per word per frame, shared by every property that reads it
  let lookFrame = -1, looks: WordLook[] = [];
  const lookOf = (i: number) => {
    const f = speechFrame();
    if (f !== lookFrame) { const a = activeWordIndex(card, speechMs()); looks = card.words.map((w, j) => wordLook(style, w, j, a, f, o.fps)); lookFrame = f; }
    return looks[i];
  };

  const words = card.words.map((w, i) => {
    const txt = new Txt({
      text: upperText(w.text, lang, style.caseMode === "upper"),
      fontFamily: family(style.font), fontWeight: weight(style.font, style.weight), fontSize: style.fontSize,
      lineHeight: `${Math.round((rtl ? Math.max(style.lineHeight, 1.5) : style.lineHeight) * 100)}%`,
      letterSpacing: rtl ? 0 : style.letterSpacing, textDirection: rtl ? "rtl" : "ltr",
      fill: () => lookOf(i).color, opacity: () => lookOf(i).opacity, scale: () => lookOf(i).scale,
      stroke: style.stroke, lineWidth: () => (style.strokeWidth > 0 && !lookOf(i).pill ? style.strokeWidth : 0), strokeFirst: true,
      ...(style.shadow ? CAPTION_SHADOW : {}),
      shadowColor: () => (lookOf(i).glow ? style.highlight : style.shadow ? CAPTION_SHADOW.shadowColor : "rgba(0,0,0,0)"),
      shadowBlur: () => (lookOf(i).glow ? 14 : style.shadow ? CAPTION_SHADOW.shadowBlur : 0),
      // a right-to-left word's cache box is measured for left-to-right text: give it room
      cachePadding: isRtlText(w.text) ? [style.fontSize, style.fontSize * 4] : 0,
    });
    // the pill behind the spoken word and the underline under an emphasised or spoken one: neither takes room in the flow
    const pill = new Rect({ layout: false, fill: style.highlight, radius: em(0.3), width: () => txt.width() + em(0.36), height: () => txt.height(), opacity: () => (lookOf(i).pill ? 1 : 0) });
    const underline = new Rect({ layout: false, fill: style.highlight, y: () => txt.height() / 2 + em(0.12), width: () => txt.width(), height: em(0.09), opacity: () => (lookOf(i).underline ? 1 : 0) });
    return new Layout({ layout: true, padding: () => [0, lookOf(i).pill ? em(0.18) : 0], children: [pill, txt, underline] });
  });

  const row = new Layout({
    layout: true, direction: rtl ? "row-reverse" : "row", wrap: "wrap", gap: em(0.32), padding: [0, em(0.16)],
    justifyContent: style.align === "left" ? "start" : "center", alignItems: "center", children: words,
  });
  const lines: Node[] = [row];
  if (second) {
    // the same card in another language, smaller, under the words (its 0.94 opacity lives in the colour: an opacity would cache it)
    const colour = rgba(style.secondColor ?? style.color, 0.94);
    lines.push(paragraph({
      runs: [{ text: second.text }], font: second.font, weight: 700, size: Math.round(style.fontSize * style.secondScale),
      maxWidth: Math.round(block.width - (box ? 2 * box.padX : 0)), fill: colour, align: style.align, lineHeight: second.rtl ? 1.6 : 1.25, letterSpacing: 0, rtl: second.rtl,
      line: { stroke: style.stroke, lineWidth: style.strokeWidth > 0 ? style.strokeWidth : 0, strokeFirst: true, ...(style.shadow ? CAPTION_SHADOW : {}) },
      block: { marginTop: em(0.3) },
    }));
  }
  const inner = new Rect({
    layout: true, direction: "column", alignItems: style.align === "left" ? "start" : "center", maxWidth: block.width,
    padding: box ? [box.padY, box.padX] : 0, radius: box ? box.radius : 0,
    fill: box ? rgba(style.boxColor, style.boxOpacity) : undefined,
    ...(glass ? { stroke: "rgba(255,255,255,0.22)", lineWidth: 1, shadowColor: "rgba(0,0,0,0.35)", shadowBlur: 40, shadowOffset: [0, 12] } : {}),
    children: lines,
  });
  return new Layout({
    layout: true, width: block.width, justifyContent: style.align === "left" ? "start" : "center",
    position: () => [block.left + block.width / 2 - 540, block.top - HEIGHT / 2 + cardLift(style, enter())],
    opacity: () => (active() ? enter() : 0),
    children: [inner],
  });
}

/** Every caption card, prebuilt; only the active one is visible. Mirrors Captions in the Remotion reel. */
export function captionsLayer(o: CaptionsInput): Node {
  return new Layout({ width: 1080, height: HEIGHT, children: o.clip.captions.map((card, i) => cardNode(o.clip, card, i, o)) });
}
